/**
 * Sprint-104d-full — TrackingSummaryCards.
 *
 * Four-up tile row above the timeline:
 *   1. ETA (from /api/orders/{tid}/eta)
 *   2. Status
 *   3. Distance (km)
 *   4. Parcel count (items.length or 1)
 *
 * Mobile-first: stacks 2×2 on <sm, 4-up on sm+.
 */
import { Clock, Activity, Route, Boxes } from "lucide-react";

function Tile({ icon: Icon, label, value, tone = "slate", testId }) {
  const tones = {
    slate:   "bg-slate-50 text-slate-800 border-slate-100",
    orange:  "bg-orange-50 text-orange-800 border-orange-100",
    emerald: "bg-emerald-50 text-emerald-800 border-emerald-100",
    sky:     "bg-sky-50 text-sky-800 border-sky-100",
  };
  return (
    <div
      className={`rounded-2xl border p-4 flex items-start gap-3 ${tones[tone] || tones.slate}`}
      data-testid={testId}
    >
      <div className="shrink-0 w-9 h-9 rounded-xl bg-white flex items-center justify-center shadow-sm">
        <Icon className="h-4.5 w-4.5 text-current opacity-90" />
      </div>
      <div className="min-w-0">
        <p className="text-[10px] uppercase tracking-wider font-bold opacity-70">{label}</p>
        <p className="font-['Outfit'] text-lg font-bold leading-tight mt-0.5 truncate">{value || "—"}</p>
      </div>
    </div>
  );
}

export default function TrackingSummaryCards({ order, eta }) {
  if (!order) return null;
  const statusLabel = String(order.status || "—").replace(/_/g, " ");
  const distKm = order.road_distance_km || order.distance_km || eta?.distance_km;
  const parcelCount = Array.isArray(order.items) && order.items.length > 0 ? order.items.length : 1;

  let etaText = "—";
  if (eta) {
    if (eta.eta_minutes === 0 && order.status === "delivered") etaText = "Delivered";
    else if (eta.eta_minutes > 0) etaText = `~${eta.eta_minutes} min`;
    else if (eta.eta_human) etaText = eta.eta_human;
  }

  return (
    <div
      className="grid grid-cols-2 sm:grid-cols-4 gap-3"
      data-testid="tracking-summary-cards"
    >
      <Tile icon={Clock}   label="ETA"          value={etaText}                              tone="orange"  testId="tracking-card-eta" />
      <Tile icon={Activity} label="Status"      value={<span className="capitalize">{statusLabel}</span>}    tone="emerald" testId="tracking-card-status" />
      <Tile icon={Route}   label="Distance"     value={distKm ? `${Number(distKm).toFixed(1)} km` : "—"}     tone="sky"     testId="tracking-card-distance" />
      <Tile icon={Boxes}   label="Parcels"      value={`${parcelCount}`}                                     tone="slate"   testId="tracking-card-parcels" />
    </div>
  );
}
